/**
 * 関連法令マスタ (顧客別) の Redux slice。
 *
 * 管理業務 (ManagementTask) の「関連法令」プルダウンのユーザー追加分を、
 * 顧客横断 (= 同じクライアントの他物件でも引き継げる) で保存するための store。
 * 構造は groupOptionManagementSlice と完全に同じ (clientMasters の薄いラッパ)。
 *
 * 規約: 追加はモーダル内ではローカル state のみで行い、管理業務本体の保存
 * (createManagementTask / updateManagementTask) が成功した後に
 * upsertRelatedLawManagement を 1 度だけ dispatch する。
 * 修繕モーダルのカテゴリ追加 (categoryManagementSlice) と同じ方針。
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type RelatedLawManagementRecord = {
  related_law_management_id: string;
  client_id: string;
  related_laws: string[];
  created_at: string;
  updated_at?: string;
};

export const fetchRelatedLawManagement =
  createAsyncThunk<RelatedLawManagementRecord | null>(
    "relatedLawManagement/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await ipponApi.get(
          `${BaseURL}/api/v1/related-law-management`,
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        return list.length > 0
          ? (list[0] as RelatedLawManagementRecord)
          : null;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data || "Failed to fetch related law management",
        );
      }
    },
  );

export const createRelatedLawManagement = createAsyncThunk<
  RelatedLawManagementRecord,
  { related_laws?: string[] }
>("relatedLawManagement/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/related-law-management`,
      body,
    );
    return res.data.data as RelatedLawManagementRecord;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data || "Failed to create related law management",
    );
  }
});

export const updateRelatedLawManagement = createAsyncThunk<
  RelatedLawManagementRecord,
  { related_law_management_id: string; related_laws?: string[] }
>(
  "relatedLawManagement/update",
  async ({ related_law_management_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/related-law-management/${related_law_management_id}`,
        body,
      );
      return res.data.data as RelatedLawManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update related law management",
      );
    }
  },
);

export const upsertRelatedLawManagement = createAsyncThunk<
  RelatedLawManagementRecord,
  { existingId?: string | null; related_laws?: string[] }
>(
  "relatedLawManagement/upsert",
  async ({ existingId, ...body }, { dispatch, rejectWithValue }) => {
    try {
      if (existingId) {
        const resultAction: any = await dispatch(
          updateRelatedLawManagement({
            related_law_management_id: existingId,
            ...body,
          }),
        );
        if (updateRelatedLawManagement.rejected.match(resultAction)) {
          throw resultAction.payload || new Error("update failed");
        }
        return resultAction.payload as RelatedLawManagementRecord;
      }
      const resultAction: any = await dispatch(
        createRelatedLawManagement(body),
      );
      if (createRelatedLawManagement.rejected.match(resultAction)) {
        throw resultAction.payload || new Error("create failed");
      }
      return resultAction.payload as RelatedLawManagementRecord;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

type RelatedLawManagementState = {
  loading: boolean;
  error: boolean;
  record: RelatedLawManagementRecord | null;
};

const initialState: RelatedLawManagementState = {
  loading: false,
  error: false,
  record: null,
};

export const relatedLawManagementSlice = createSlice({
  name: "relatedLawManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRelatedLawManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchRelatedLawManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(fetchRelatedLawManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(createRelatedLawManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(updateRelatedLawManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(upsertRelatedLawManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      });
  },
});

export default relatedLawManagementSlice.reducer;
